import React from "react";

import { useState, useRef, useEffect } from "react";

import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import { UploadCloud, FileIcon, X } from "lucide-react";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import { Textarea } from "components/Graphs/TextArea";
import { FlatCard } from "components/UI-lib";

// Constants
const MAX_SCRIPT_SIZE = 16 * 1024; // 16 KiB in bytes
const WARNING_THRESHOLD = 0.9; // Show warning at 90% of limit

// Helper function to calculate script size in bytes
const calculateScriptSize = (content: string): number => {
  return new Blob([content]).size;
};

// Helper function to format bytes to human-readable format
const formatBytes = (bytes: number): string => {
  const kib = bytes / 1024;
  return `${kib.toFixed(2)} KiB`;
};

const DEFAULT_PLACEHOLDER = `Paste your setup script here... (Must start with #!/bin/bash, max size: ${formatBytes(
  MAX_SCRIPT_SIZE,
)})`;

export const DEFAULT_LIFECYCLE_SCRIPT = "#!/bin/bash\n\n";

export const LifeCycleScript: React.FC<{
  script: string;
  onSetScript: (script: string) => void;
  lifecycleScriptTab: "upload" | "paste";
  setLifecycleScriptTab: (tab: "upload" | "paste") => void;
  lifecycleScriptFile: File | null;
  setLifecycleScriptFile: (file: File | null) => void;
  saveDescription: string;
  hideTitle?: boolean;
  experimentalFlag?: boolean;
  onValidationChange?: (isValid: boolean) => void;
  title?: string;
  description?: string;
  placeholder?: string;
  autoFocus?: boolean;
}> = ({
  script,
  onSetScript,
  lifecycleScriptTab,
  setLifecycleScriptTab,
  lifecycleScriptFile,
  setLifecycleScriptFile,
  saveDescription,
  hideTitle,
  experimentalFlag,
  onValidationChange,
  title = "Run a Setup Script",
  description = "Add a script that will run after the environment is created and the VM is ready.",
  placeholder,
  autoFocus,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Local state for script content - initialize from parent script
  const [localScriptContent, setLocalScriptContent] = useState(script);

  // Validation state
  const [validationError, setValidationError] = useState<string>("");
  const [scriptSize, setScriptSize] = useState<number>(0);

  // Validation function
  const validateScript = (scriptContent: string): boolean => {
    if (!scriptContent.trim()) {
      setValidationError("");
      setScriptSize(0);
      return true; // Empty script is valid (no validation needed)
    }

    // Calculate size
    const size = calculateScriptSize(scriptContent);
    setScriptSize(size);

    // Check size limit
    if (size > MAX_SCRIPT_SIZE) {
      setValidationError(
        `Script exceeds maximum size limit. Your script is ${formatBytes(
          size,
        )} but the limit is ${formatBytes(
          MAX_SCRIPT_SIZE,
        )}. Please reduce your script size.`,
      );
      return false;
    }

    // Check shebang
    if (!scriptContent.trim().startsWith("#!/bin/bash")) {
      setValidationError("Script must start with #!/bin/bash");
      return false;
    }

    setValidationError("");
    return true;
  };

  // Initialize local state based on current tab and whether we have a file or content
  useEffect(() => {
    if (lifecycleScriptFile) {
      // If we have a file, clear any script content (file takes precedence)
      setLocalScriptContent("");
    } else if (lifecycleScriptTab === "paste") {
      // If no file and we're on paste tab, show the script content
      setLocalScriptContent(script);
    } else {
      // If no file and on upload tab, clear content
      setLocalScriptContent("");
    }
  }, [script, lifecycleScriptTab, lifecycleScriptFile]);

  // Validate script when it changes from parent
  useEffect(() => {
    if (script) {
      const isValid = validateScript(script);
      onValidationChange?.(isValid);
    } else {
      // Empty script is valid
      setValidationError("");
      onValidationChange?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script]);

  const handleFileChange = (selectedFile: File | null) => {
    if (selectedFile) {
      setLifecycleScriptFile(selectedFile);
      setLocalScriptContent(""); // Clear pasted content locally
      // Read file content and update parent immediately
      readFileAndSubmit(selectedFile);
    }
  };

  const readFileAndSubmit = async (file: File) => {
    setIsLoading(true);
    try {
      const fileContent = await file.text();
      if (fileContent) {
        const isValid = validateScript(fileContent);
        onSetScript(fileContent); // Send file content to parent container
        onValidationChange?.(isValid); // Notify parent of validation state
      }
    } catch (error) {
      console.error("Error reading file:", error);
    }
    setIsLoading(false);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange(droppedFile);
    }
  };

  const handleRemoveFile = () => {
    setLifecycleScriptFile(null);
    setLocalScriptContent(""); // Clear local content
    onSetScript(""); // Clear parent script state
    setValidationError(""); // Clear validation error
    setScriptSize(0); // Reset size
    onValidationChange?.(true); // Notify parent that validation is now valid (empty is valid)
  };

  return (
    <FlatCard>
      {(!hideTitle || description) && (
        <div className="flex flex-col mb-8">
          {!hideTitle && (
            <h2 className="text-lg font-medium text-primary">{title}</h2>
          )}
          {description && (
            <p className="text-sm text-secondary">{description}</p>
          )}
        </div>
      )}
      <div>
        <Tabs
          value={lifecycleScriptTab}
          onValueChange={(value) =>
            setLifecycleScriptTab(value as "upload" | "paste")
          }
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="paste">Paste Script</TabsTrigger>
            <TabsTrigger value="upload">File Upload</TabsTrigger>
          </TabsList>
          <TabsContent value="upload" className="pt-4">
            {lifecycleScriptFile ? (
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                <div className="flex items-center gap-3 overflow-hidden">
                  <FileIcon className="w-6 h-6 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col overflow-hidden">
                    <span className="font-medium text-sm truncate">
                      {lifecycleScriptFile.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatBytes(lifecycleScriptFile.size)}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={handleRemoveFile}
                  className="rounded-full"
                >
                  <X className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ) : (
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`relative flex flex-col items-center justify-center w-full p-10 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  isDragging
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50"
                }`}
                onClick={() => {
                  fileInputRef.current?.click();
                }}
              >
                <UploadCloud className="w-12 h-12 text-muted-foreground" />
                <p className="mt-4 text-sm font-medium text-center">
                  Drag & drop your script here or click to browse
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Any text-based file is supported. Maximum size:{" "}
                  {formatBytes(MAX_SCRIPT_SIZE)}
                </p>
                <Input
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  ref={fileInputRef}
                  type="file"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={(e) =>
                    handleFileChange(e.target.files ? e.target.files[0] : null)
                  }
                  disabled={isLoading}
                />
              </div>
            )}
          </TabsContent>
          <TabsContent value="paste" className="pt-4">
            <Textarea
              autoFocus={autoFocus}
              placeholder={placeholder ?? DEFAULT_PLACEHOLDER}
              className="min-h-[200px] font-mono text-sm"
              value={localScriptContent}
              onChange={(e) => {
                setLocalScriptContent(e.target.value);
                setLifecycleScriptFile(null); // Clear file when typing
                const isValid = validateScript(e.target.value);
                onSetScript(e.target.value); // Update parent immediately
                onValidationChange?.(isValid); // Notify parent of validation state
              }}
              disabled={isLoading}
            />
          </TabsContent>
        </Tabs>

        {/* Size Indicator */}
        {(lifecycleScriptFile || localScriptContent.trim()) && (
          <div className="mt-2 flex items-center justify-between text-sm">
            <span
              className={`${
                scriptSize > MAX_SCRIPT_SIZE
                  ? "text-red-600 dark:text-red-400 font-medium"
                  : scriptSize > MAX_SCRIPT_SIZE * WARNING_THRESHOLD
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-muted-foreground"
              }`}
            >
              Script size: {formatBytes(scriptSize)} /{" "}
              {formatBytes(MAX_SCRIPT_SIZE)}
            </span>
            {scriptSize > MAX_SCRIPT_SIZE * WARNING_THRESHOLD &&
              scriptSize <= MAX_SCRIPT_SIZE && (
                <span className="text-xs text-yellow-600 dark:text-yellow-400">
                  Approaching size limit
                </span>
              )}
          </div>
        )}

        {/* Validation Error Display */}
        {validationError && (
          <div className="mt-2 text-red-500 text-sm p-2 bg-red-50 dark:bg-red-900/20 rounded">
            {validationError}
          </div>
        )}
      </div>
      <div>
        {!isLoading &&
          (lifecycleScriptFile || localScriptContent.trim()) &&
          saveDescription &&
          !validationError && (
            <div className="text-sm text-highlight mt-2">{saveDescription}</div>
          )}
        {/* <Button
          onClick={handleSubmit}
          disabled={(!file && !scriptContent.trim()) || isLoading}
          className="w-full mt-4"
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isLoading ? "Uploading..." : "Apply"}
        </Button> */}
      </div>
    </FlatCard>
  );
};
